import { Suspense, Component } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ReloadOutlined } from '@ant-design/icons';

// project import
import Loader from './Loader';

// ==============================|| CHUNK LOAD ERROR BOUNDARY ||============================== //

/**
 * ChunkLoadError 발생 시 새로고침 UI를 제공하는 에러 바운더리
 * - Service Worker 캐시 불일치로 인한 청크 로드 실패 처리
 * - 사용자에게 새로고침 옵션 제공
 */
class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // ChunkLoadError 또는 청크 로드 관련 에러 감지
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to fetch dynamically imported module');

    if (isChunkError) {
      return { hasError: true, error };
    }
    // 다른 에러는 상위로 전파
    throw error;
  }

  handleRefresh = () => {
    // 캐시 무효화를 위해 하드 리프레시
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            gap: 2,
            p: 3
          }}
        >
          <Typography variant="h6" color="text.secondary">
            페이지 로드 중 문제가 발생했습니다
          </Typography>
          <Typography variant="body2" color="text.secondary">
            새로고침하면 해결됩니다
          </Typography>
          <Button variant="contained" startIcon={<ReloadOutlined />} onClick={this.handleRefresh} sx={{ mt: 1 }}>
            새로고침
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

// ==============================|| LOADABLE - LAZY LOADING ||============================== //

const Loadable = (Component) => (props) => (
  <ChunkErrorBoundary>
    <Suspense fallback={<Loader />}>
      <Component {...props} />
    </Suspense>
  </ChunkErrorBoundary>
);

export default Loadable;
